import { useState } from 'react'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ApiError, assistant, session } from '../../lib/api'

const SOURCE_NAMES: Record<string, string> = {
  file: 'a secret mounted into the container',
  database: 'this console',
  none: 'nowhere — none is set'
}

/**
 * The OpenRouter credential for this deployment.
 *
 * Three things about this card are deliberate and each of them is the answer to a question an
 * operator would otherwise have to guess at.
 *
 * It says where the key in use comes from. A deployment that mounts a secret file keeps using it, and
 * the field is read-only rather than editable-but-ignored, because a form that accepts a value and
 * then does nothing with it is worse than one that explains itself.
 *
 * It never shows the key. What comes back is the last four characters, which is enough to tell one
 * key from another and not enough to be one — the row holds ciphertext wrapped under the deployment's
 * key-encryption key, and handing the key back over an API would undo the reason for that.
 *
 * And it is only rendered for an owner. Not because the endpoint trusts the browser — it does not,
 * the policy is enforced on the server — but because showing an analyst a control that will answer
 * 403 is a way of wasting their time. Since this became a tab of its own, `SettingsPage` hides the
 * tab as well: this guard would otherwise leave an analyst looking at an empty panel, which is a
 * worse answer than the missing card it used to be.
 *
 * The heading is the tab's, not this card's (D-429).
 */
export function ProviderCredentialCard() {
  // The role is checked out here, before the card that holds the hooks exists, so an analyst opening
  // this screen makes no request at all. Checking it inside would still hide the card, but only after
  // firing a fetch whose answer is a guaranteed 403.
  const roles = session.identity()?.roles ?? []
  if (!roles.includes('owner')) return null

  return <OwnerCredentialCard />
}

function OwnerCredentialCard() {
  const status = useAsync(() => assistant.credential(), [])
  const [key, setKey] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Removing the key takes the assistant away from every tenant on this deployment, and the key is
  // never shown again - so there is nothing on this screen to put back, only a key somebody has to
  // go and find. One button, next to Save, with no question in front of it.
  const [asking, setAsking] = useState(false)

  async function run(work: () => Promise<string>) {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      setNotice(await work())
      status.reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The request failed.')
    } finally {
      setBusy(false)
    }
  }

  const save = () =>
    run(async () => {
      await assistant.saveCredential(key.trim())
      setKey('')
      return 'Saved. The assistant will use this key from the next request.'
    })

  const clear = () =>
    run(async () => {
      setAsking(false)
      await assistant.clearCredential()
      setKey('')
      return 'Removed. The assistant is unavailable until a key is set.'
    })

  const value = status.value

  // The development stub answers from a script and calls nobody, so it is "configured" with no
  // credential at all. Without this the card would report a key supplied by nowhere, which reads as a
  // bug in the card rather than as the deployment being deliberately offline.
  const stub = value?.provider === 'stub'

  return (
    <div className="card">
      <div className="card-body">
        <p className="muted" style={{ marginTop: 0 }}>
          One deployment has one account with the provider, so this key is shared by every tenant on
          this server. Which model to use and what it may spend are chosen per tenant, on the{' '}
          Assistant screen.
        </p>

        <ErrorBanner message={failure ?? status.error} />
        {notice && (
          <div className="banner success" role="status" data-testid="credential-notice">
            {notice}
          </div>
        )}
        {value?.unavailable && (
          <div className="banner warning" role="status">
            {value.unavailable}
          </div>
        )}

        <dl className="detail" data-testid="credential-status">
          <dt>Provider</dt>
          <dd className="mono">{value?.provider ?? '—'}</dd>

          <dt>Key in use</dt>
          <dd>
            {stub ? (
              <span className="muted">None is needed. This deployment answers from a script.</span>
            ) : value?.configured ? (
              <>
                Supplied by {SOURCE_NAMES[value.source] ?? value.source}
                {value.hint && <span className="mono"> · ends {value.hint}</span>}
              </>
            ) : (
              <span className="muted">None. The assistant is unavailable.</span>
            )}
          </dd>

          {value?.updatedUtc && (
            <>
              <dt>Set</dt>
              <dd className="muted">{formatUtc(value.updatedUtc)}</dd>
            </>
          )}
        </dl>

        {stub ? (
          <p className="hint" style={{ marginTop: '0.85rem' }}>
            This deployment runs the development stub, which answers from a script and calls no
            provider. Set <span className="mono">Assistant__Provider=openrouter</span> to talk to a
            real one, and a key can then be set here.
          </p>
        ) : value?.source === 'file' ? (
          <p className="hint" style={{ marginTop: '0.85rem' }}>
            This deployment mounts its key as a secret, which takes precedence over anything stored
            here. Remove <span className="mono">Assistant__ApiKeyFile</span> from the deployment to set
            the key from this screen instead.
          </p>
        ) : (
          <div className="field" style={{ marginTop: '0.85rem' }}>
            <label htmlFor="provider-key">OpenRouter API key</label>
            <input
              id="provider-key"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={key}
              disabled={busy || value?.canStore === false}
              placeholder={value?.configured ? 'Enter a new key to replace the current one' : 'sk-or-v1-…'}
              onChange={(event) => setKey(event.target.value)}
              data-testid="provider-key"
            />
            <p className="hint">
              Stored wrapped with AES-256-GCM under this deployment's key-encryption key, so a copy of
              the database is not a copy of the credential. It is never shown again.
            </p>
            <div className="toolbar" style={{ marginTop: '0.6rem' }}>
              <button
                type="button"
                className="primary"
                onClick={save}
                disabled={busy || key.trim().length === 0 || value?.canStore === false}
                data-testid="save-provider-key"
              >
                Save key
              </button>
              {value?.source === 'database' && (
                <button
                  type="button"
                  onClick={() => setAsking(true)}
                  disabled={busy}
                  data-testid="clear-provider-key"
                >
                  Remove key
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={asking}
        title="Remove the provider key"
        confirmLabel="Remove it"
        danger
        busy={busy}
        onConfirm={clear}
        onClose={() => setAsking(false)}
        data-testid="clear-provider-key-confirm"
      >
        <p>
          The assistant stops answering for every tenant on this deployment — drafting a policy,
          drafting a dictionary, reviewing terms and explaining a policy all refuse until a key is set
          again.
        </p>
        <p className="hint">
          The key is stored wrapped and is never shown, so this console cannot give it back. Putting
          it back means finding the key again at the provider.
        </p>
      </ConfirmDialog>
    </div>
  )
}
